/**
 * `expo-device` no browser. Tudo derivado do `userAgent` (e de `userAgentData` quando o
 * Chromium o oferece). `isDevice` é sempre `true`: no web não existe "simulador" — quem
 * abre a página é um aparelho real (é o que o `NotificationService` quer saber).
 */
type UADataMarca = { brand: string; version: string };
type UAData = { brands?: UADataMarca[]; mobile?: boolean; platform?: string };

const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
const uaData: UAData | undefined =
  typeof navigator !== "undefined" ? ((navigator as any).userAgentData as UAData | undefined) : undefined;

export enum DeviceType {
  UNKNOWN = 0,
  PHONE = 1,
  TABLET = 2,
  DESKTOP = 3,
  TV = 4,
}

function detectarSO(): { nome: string | null; versao: string | null } {
  let m: RegExpMatchArray | null;
  if ((m = ua.match(/Android\s([\d.]+)/i))) return { nome: "Android", versao: m[1] };
  if ((m = ua.match(/(?:iPhone|iPad|iPod).*?OS\s([\d_]+)/i))) return { nome: "iOS", versao: m[1].replace(/_/g, ".") };
  // iPadOS 13+ se apresenta como Mac com touch.
  if (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1) {
    m = ua.match(/Version\/([\d.]+)/);
    return { nome: "iPadOS", versao: m ? m[1] : null };
  }
  if ((m = ua.match(/Windows NT\s([\d.]+)/i))) return { nome: "Windows", versao: m[1] };
  if ((m = ua.match(/Mac OS X\s([\d_.]+)/i))) return { nome: "macOS", versao: m[1].replace(/_/g, ".") };
  if (/CrOS/.test(ua)) return { nome: "ChromeOS", versao: null };
  if (/Linux/i.test(ua)) return { nome: "Linux", versao: null };
  return { nome: uaData?.platform || null, versao: null };
}

function detectarNavegador(): string {
  if (/EdgA?\//.test(ua)) return "Edge";
  if (/SamsungBrowser\//.test(ua)) return "Samsung Internet";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Firefox\/|FxiOS\//.test(ua)) return "Firefox";
  if (/CriOS\/|Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return "Navegador";
}

function detectarModelo(): { marca: string | null; modelo: string | null } {
  if (/iPhone/.test(ua)) return { marca: "Apple", modelo: "iPhone" };
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1)) {
    return { marca: "Apple", modelo: "iPad" };
  }
  if (/iPod/.test(ua)) return { marca: "Apple", modelo: "iPod" };
  if (/Macintosh/.test(ua)) return { marca: "Apple", modelo: "Mac" };
  // Android: "Android 14; SM-S918B Build/..." ou "Android 10; K" (UA reduzido do Chrome).
  const m = ua.match(/Android[^;)]*;\s*([^;)]+?)(?:\sBuild\/[^;)]*)?[;)]/i);
  if (m) {
    const modelo = m[1].trim();
    const modeloUtil = modelo && modelo !== "K" && !/^wv$/i.test(modelo) ? modelo : null;
    let marca: string | null = null;
    if (modeloUtil) {
      if (/^SM-|Samsung/i.test(modeloUtil)) marca = "samsung";
      else if (/^(Pixel)/i.test(modeloUtil)) marca = "Google";
      else if (/moto|XT\d/i.test(modeloUtil)) marca = "motorola";
      else if (/Redmi|Mi\s|POCO|M\d{4}/i.test(modeloUtil)) marca = "Xiaomi";
      else if (/^(LM-|LG)/i.test(modeloUtil)) marca = "LGE";
    }
    return { marca, modelo: modeloUtil };
  }
  if (/Windows/.test(ua)) return { marca: null, modelo: "PC" };
  return { marca: null, modelo: null };
}

function detectarTipo(): DeviceType {
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1)) {
    return DeviceType.TABLET;
  }
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return DeviceType.TABLET;
  if (uaData?.mobile || /Mobi|iPhone|iPod|Android/i.test(ua)) return DeviceType.PHONE;
  if (/SmartTV|SMART-TV|TV;/i.test(ua)) return DeviceType.TV;
  if (ua) return DeviceType.DESKTOP;
  return DeviceType.UNKNOWN;
}

const so = detectarSO();
const mod = detectarModelo();

export const isDevice: boolean = true;
export const brand: string | null = mod.marca;
export const manufacturer: string | null = mod.marca;
export const modelName: string | null = mod.modelo;
export const modelId: string | null = null;
export const designName: string | null = null;
export const productName: string | null = null;
export const deviceYearClass: number | null = null;
export const totalMemory: number | null =
  typeof navigator !== "undefined" && typeof (navigator as any).deviceMemory === "number"
    ? Math.round((navigator as any).deviceMemory * 1024 * 1024 * 1024)
    : null;
export const supportedCpuArchitectures: string[] | null = null;
export const osName: string | null = so.nome;
export const osVersion: string | null = so.versao;
export const osBuildId: string | null = null;
export const osInternalBuildId: string | null = null;
export const osBuildFingerprint: string | null = null;
export const platformApiLevel: number | null = null;
/** WEB: "<Navegador> em <SO>" — não existe nome do aparelho dado pelo usuário no browser. */
export const deviceName: string | null = `${detectarNavegador()}${so.nome ? ` em ${so.nome}` : ""}`;
export const deviceType: DeviceType = detectarTipo();

export async function getDeviceTypeAsync(): Promise<DeviceType> {
  return deviceType;
}

export async function isRootedExperimentalAsync(): Promise<boolean> {
  return false;
}

export async function getUptimeAsync(): Promise<number> {
  // WEB: não há uptime do aparelho; devolve o tempo desde o carregamento da página.
  return typeof performance !== "undefined" ? performance.now() : 0;
}

export async function getMaxMemoryAsync(): Promise<number> {
  return totalMemory ?? Number.MAX_SAFE_INTEGER;
}

export async function isSideLoadingEnabledAsync(): Promise<boolean> {
  return false;
}

export async function getPlatformFeaturesAsync(): Promise<string[]> {
  return [];
}

export async function hasPlatformFeatureAsync(_feature: string): Promise<boolean> {
  return false;
}
